// @ts-check
import classNames from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';
import React, { useEffect } from 'react';
import SvgImage from './images/SvgImage';

import './SpeedBump.scss';

const SAFE_SITE = new RegExp(
	/(127.0.0.1|\.ozk|ozkwebsite|bankozarks|fisglobal|peopleclick|iremitweb|azurewebsites|localhost|secureinternetbank\.com|\.personalcard\.net|\.wealthaccess\.com|assets\.contentstack\.io)|^(tel:|mailto:)/
);

/**
 * @typedef SpeedBumpOptions
 * @property {'primary' | 'secondary' | 'tertiary'} button_style
 * @property {string} button_text
 * @property {string} copy The main warning copy.
 * @property {string} heading The main heading.
 * @property {import('../CSTypes').CSFile} image The image to show on the left.

 * @typedef SpeedBumpLink
 * @property {string} href
 * @property {string} target

 * @typedef SpeedBumpState
 * @property {SpeedBumpLink | null} [link=null] The link of the speedbump. If missing, not open.
 * @property {boolean} didAttach A value indicating whether the component has run against tags.
 */
/** @type {import('~/types/React').ReactFC<>} */
const SpeedBump = () => {
	/** @type {SpeedBumpState} */
	const initialState = {
		link: null,
		didAttach: false,
	};

	const [state, setState] = React.useState(initialState);

	// Do ContentStack query
	/** @type {{ ozkSpeedBump: SpeedBumpOptions }} */
	const { ozkSpeedBump } = useStaticQuery(graphql`
		query GlobalSpeedBump {
			ozkSpeedBump {
				uid
				_version

				heading
				copy
				button_style
				button_text
				image {
					...AssetFragment
				}
			}
		}
	`);

	//#region Actions
	/** @type {(href?: string, target?: string) => void} */
	function setLink(href, target) {
		setState((prev) => ({ ...prev, link: { href, target } }));
	}
	/** @type {import('react').MouseEventHandler} */
	function reset(e) {
		if (e.target === e.currentTarget) {
			document.body.style.overflow = 'auto';
			setState((prev) => ({ ...prev, link: null }));
		}
	}
	/** @type {import('react').MouseEventHandler} */
	function follow(e) {
		if (state.link?.target?.length > 0) {
			window.open(state.link.href, state.link.target);
		} else {
			window.location.href = encodeURI(state.link.href);
		}

		reset(e);
	}
	//#endregion

	// Attach if we need to
	useEffect(() => {
		/** @type {() => void} */
		function attachToLinkTags() {
			for (const tag of document.querySelectorAll('a')) {
				const isNotYetAttached = !tag.classList.contains('speed-bump-attached');
				const isSafeSiteNoMatch = !tag.href.match(SAFE_SITE);

				if (isNotYetAttached && isSafeSiteNoMatch) {
					tag.classList.add('speed-bump-attached');

					tag.onclick = (e) => {
						e.preventDefault();
						setLink(tag.href, tag.target);
					};
				}
			}

			setState((prev) => ({ ...prev, didAttach: true }));
		}

		const attachInterval = setInterval(attachToLinkTags, 1000);

		return () => {
			clearInterval(attachInterval);
		};
	}, []);

	// Render nothing when not open.
	if (!state.link) {
		return null;
	}

	// Prevent background scrolling
	document.body.style.overflow = 'hidden';

	return (
		<div className="speed-bump animated fadeIn theme-minimal">
			<div className="speed-bump-wrapper" onClick={reset}>
				<div className="speed-bump-modal">
					<div className="speed-bump-close-icon icon" aria-label="Close Dialog" onClick={reset}>
						close
					</div>
					<h2>{ozkSpeedBump?.heading}</h2>

					<SvgImage
						className="speed-bump-image"
						options={{
							alt_text: 'Speed Bump Graphic',
							svg: ozkSpeedBump.image,
						}}
					/>

					{/* <img className="speed-bump-image" src="/assets/images/bumper.png" alt="Bumper Graphic" aria-hidden /> */}
					<p>{ozkSpeedBump?.copy}</p>
					<button
						className={classNames(['btn', !!ozkSpeedBump?.button_style && `btn--${ozkSpeedBump.button_style}`])}
						onClick={follow}
					>
						{ozkSpeedBump?.button_text}
					</button>
				</div>
			</div>
		</div>
	);
};

export default SpeedBump;
